export const surveyState = {
  isLoading: false,
  questions: [],
  activeQuestion: -1,
  // Multiple surveys support
  availableSurveys: [],
  selectedSurvey: null,
  currentSurveyIndex: 0,
  surveyProgress: {},
  isTakingSurvey: false,
  // Manage
  loadingList: false,
  teamSurveys: [],
  hasActiveSurvey: false,
  activeSurvey: 0,
  createNewSurvey: false,
  isCreating: false,
  reload: false,
  addQuestions: false,
  prePopulatedQuestions: [],
  teamQuestions: [],
  selectedQuestions: [],
  isSubmittingAns: false,
  surveyPage: 1,
  surveyTotal: 0,
  surveyMeta: null,
};

export const SURVEY_STATUS = {
  ACTIVE: 1,
  INACTIVE: 0,
  DRAFT: 2,
};

export const surveyReducer = (state, action) => {
  switch (action.type) {
    case "isLoading":
      return { ...state, isLoading: action.value };
    case "setSurveyQuestions":
      return {
        ...state,
        questions:
          action.value.filter((x) => x.isAnswered).length > 0
            ? []
            : action.value,
        activeQuestion: action.value.filter((x) => !x.isAnswered)[0] || -1,
        loading: false,
      };
    case "setMultipleSurveys":
      return handleMultipleSurveys(state, action.value);
    case "selectSurvey":
      return selectSurvey(state, action.value);
    case "startSurvey":
      return { 
        ...state, 
        isTakingSurvey: true, 
        selectedSurvey: action.value ? state.availableSurveys.find(s => s.survey_id === action.value) : null 
      };
    case "completeSurvey":
      return completeSurvey(state, action.value);
    case "setActivequestion":
      return { ...state, activeQuestion: action.value };
    case "loadingList":
      return { ...state, loadingList: action.value };
    case "setTeamSurveys":
      return checkWithHasActiveSurvey(state, action.value);
    case "appendTeamSurveys":
      return appendSurveys(state, action.value);
    case "manageActiveSurvey":
      return { ...state, activeSurvey: Number(action.value) };
    case "handleCreateNewSurvey":
      return { ...state, createNewSurvey: action.value };
    case "setIsCreating":
      return { ...state, isCreating: action.value };
    case "setReload":
      return { ...state, reload: action.value };
    case "handleAddQuestions":
      return { ...state, addQuestions: action.value };
    case "allQuestionsToAdd":
      return formateAllQuestionstoAdd(state, action.value);
    case "setSelectedQuestions":
      return { ...state, selectedQuestions: action.value };
    case "setIsSubmittingAns":
      return { ...state, isSubmittingAns: action.value };
    case "setSelectedSurveyQuestions":
      return { ...state, selectedSurveyQuestion: action.value };
    case "setSurveyPage":
      return { ...state, surveyPage: action.value };
    case "setSurveyTotal":
      return { ...state, surveyTotal: action.value };
    case "setSurveyMeta":
      return { ...state, surveyMeta: action.value };
    case "incrementSurveyPage":
      return { ...state, surveyPage: state.surveyPage + 1 };
    case "clearSurveys":
      return { ...state, teamSurveys: [], surveyPage: 1, surveyTotal: 0, surveyMeta: null };
    default:
      return state;
  }
};

const checkWithHasActiveSurvey = (state, data) => {
  // Preserve activeSurvey if it exists and the survey is still in the list
  const currentActiveSurvey = state.activeSurvey > 0 
    ? (data.find((s) => s.id === state.activeSurvey) ? state.activeSurvey : 0)
    : 0;
  
  return {
    ...state,
    // Preserve activeSurvey if the survey still exists, otherwise reset to 0
    activeSurvey: currentActiveSurvey,
    hasActiveSurvey:
      data.filter((v) => v.status === SURVEY_STATUS.ACTIVE).length > 0
        ? true
        : false,
    teamSurveys: data,
    loadingList: false,
  };
};

const appendSurveys = (state, newSurveys) => {
  return {
    ...state,
    teamSurveys: [...state.teamSurveys, ...newSurveys],
    loadingList: false,
  };
};

const formateAllQuestionstoAdd = (state, data) => {
  const prePopulated = data.questions.map((r) => ({
    id: r.id,
    questions: r.questions,
    type: r.type,
    isCustomCreated: 0,
  }));
  const teamQuestions = data.teamQuestion.map((r) => ({
    id: r.id,
    questions: r.question,
    type: r.type,
    isCustomCreated: 1,
  }));
  return {
    ...state,
    prePopulatedQuestions: prePopulated,
    teamQuestions: teamQuestions,
  };
};

// Helper functions for multiple surveys
const handleMultipleSurveys = (state, surveys) => {
  const availableSurveys = surveys.map(survey => ({
    ...survey,
    totalQuestions: survey.questions.length,
    answeredQuestions: survey.questions.filter(q => q.isAnswered).length,
    isCompleted: survey.questions.every(q => q.isAnswered),
    progress: (survey.questions.filter(q => q.isAnswered).length / survey.questions.length) * 100
  }));

  return {
    ...state,
    availableSurveys,
    hasActiveSurvey: availableSurveys.length > 0,
    isLoading: false,
  };
};

const selectSurvey = (state, surveyId) => {
  const selectedSurvey = state.availableSurveys.find(s => s.survey_id === surveyId);
  if (!selectedSurvey) return state;

  const unansweredQuestions = selectedSurvey.questions.filter(q => !q.isAnswered);
  
  return {
    ...state,
    selectedSurvey,
    questions: unansweredQuestions,
    activeQuestion: unansweredQuestions[0] || -1,
    currentSurveyIndex: state.availableSurveys.findIndex(s => s.survey_id === surveyId),
  };
};

const completeSurvey = (state, surveyId) => {
  const updatedSurveys = state.availableSurveys.map(survey => 
    survey.survey_id === surveyId 
      ? { ...survey, isCompleted: true, progress: 100 }
      : survey
  );

  return {
    ...state,
    availableSurveys: updatedSurveys,
    isTakingSurvey: false,
    selectedSurvey: null,
    questions: [],
    activeQuestion: -1,
  };
};
